import React, { useCallback, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  FormErrorMessage,
  SimpleGrid,
  Flex,
  Tooltip,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import MultiInputEntries from "components/multiInputEntries";
import { useParams } from "react-router-dom";
import { InfoIcon } from "@chakra-ui/icons";
import { Post } from "api";
import { RepeatableConfig } from "views/admin/initialSetup";
import { useConfirmDialog } from "components/confirmDialog";

interface RepeatableSetup {
  repeatableEventName: string;
  repeatableUnit: string;
  repeatableFinishedByAM: string;
}

interface RoasGoal {
  dx: string;
  roasGoal: string;
}

interface FormData {
  repeatableSetup: RepeatableSetup;
  roasIapShare: string;
  revenueEventName: string;
  isRevenueValueShared: boolean;
}

type AppDetailRouteParams = {
  appId: string;
  partnerId: string;
};

const RepeatableSetupForm: React.FC<{
  platformId: number;
  initialData: RepeatableConfig[];
}> = ({ platformId, initialData }) => {
  const toast = useToast();
  const { ConfirmDialog, openDialog } = useConfirmDialog({
    title: "Confirm",
    description:
      "Are you sure you want to submit your results? This can't be undone.",
  });
  const params = useParams<AppDetailRouteParams>();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: initialData
      ? {
          isRevenueValueShared: initialData?.[0]?.isRevenueValueShared ?? false,
          repeatableSetup: {
            repeatableEventName:
              initialData?.[0]?.repeatableSetup.repeatableEventName ?? "",
            repeatableUnit:
              initialData?.[0]?.repeatableSetup.repeatableUnit ?? "",
            repeatableFinishedByAM:
              initialData?.[0]?.repeatableSetup.repeatableFinishedByAM.toString() ??
              "",
          },
          revenueEventName: initialData?.[0]?.revenueEventName ?? "",
          roasIapShare: initialData?.[0]?.roasIapShare.toString() ?? "",
        }
      : {
          repeatableSetup: {
            repeatableEventName: "",
            repeatableUnit: "",
            repeatableFinishedByAM: "",
          },
          roasIapShare: "",
          revenueEventName: "",
          isRevenueValueShared: false,
        },
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [roasGoals, setRoasGoals] = useState<RoasGoal[]>(
    initialData?.[0]?.roasGoals.map((g) => ({
      dx: g.dx.toString(),
      roasGoal: (g.roasGoal * 100).toString(),
    })) ?? []
  );

  const getIDs = (): Array<number | string | null> | null => {
    const parsedPartnerId = parseInt(params.partnerId, 10);
    const appId = params.appId;

    if (isNaN(parsedPartnerId) || !parsedPartnerId || !appId) {
      return null;
    }

    return [parsedPartnerId, appId];
  };

  const [partnerId, appId] = getIDs();

  const onSubmit = async (data: FormData) => {
    const result = await openDialog();
    if (!result) return;

    const body = {
      ...data,
      repeatableSetup: {
        ...data.repeatableSetup,
        repeatableFinishedByAM: parseInt(
          data.repeatableSetup.repeatableFinishedByAM
        ),
      },
      roasIapShare: data.roasIapShare ? parseFloat(data.roasIapShare) : null,
      revenueEventName: data.revenueEventName ? data.revenueEventName : null,
      roasGoals: roasGoals.map((g) => ({
        dx: parseInt(g.dx),
        roasGoal: parseInt(g.roasGoal, 10) / 100,
      })),
      partnerId,
      appId,
      platformId,
      isSubmission: true,
    };

    const URL = "/manage/partner/app/setup/repeatable";
    setIsSubmitting(true);
    try {
      const response = await Post(URL, body);
      if (response.err) {
        toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Success",
        description: "Setup submitted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSave = async (data: FormData) => {
    const body = {
      ...data,
      repeatableSetup: {
        ...data.repeatableSetup,
        repeatableFinishedByAM: parseInt(
          data.repeatableSetup.repeatableFinishedByAM
        ),
      },
      revenueEventName: data.revenueEventName ? data.revenueEventName : null,
      roasIapShare: data.roasIapShare ? parseFloat(data.roasIapShare) : null,
      roasGoals: roasGoals.map((g) => ({
        dx: parseInt(g.dx),
        roasGoal: parseInt(g.roasGoal, 10) / 100,
      })),
      partnerId,
      appId,
      platformId,
      isSubmission: false,
    };

    const URL = "/manage/partner/app/setup/repeatable";
    setIsSaving(true);
    try {
      const response = await Post(URL, body);
      if (response.err) {
        toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Success",
        description: "Setup saved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addNewGoalItem = useCallback(() => {
    setRoasGoals((_roasGoals) => [..._roasGoals, { dx: "", roasGoal: "" }]);
  }, []);

  const handleGoalsFieldChange = useCallback(
    (value: string, index: number, type: string) => {
      if (type === "dx" && parseInt(value, 10) < 0) return;

      if (type === "roasGoal" && parseInt(value, 10) >= 100) return;

      setRoasGoals((_roasGoals) => [
        ..._roasGoals.map((goal, idx) =>
          index === idx
            ? {
                dx: type === "dx" ? value : goal.dx,
                roasGoal: type === "roasGoal" ? value : goal.roasGoal,
              }
            : goal
        ),
      ]);
    },
    []
  );

  const handleGoalsRemove = useCallback((index: number) => {
    return setRoasGoals((prevItems) =>
      [...prevItems].filter((_, i) => i !== index)
    );
  }, []);

  return (
    <SimpleGrid
      columns={2}
      bgColor="white"
      borderRadius={"lg"}
      padding={8}
      gap={4}
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <Controller
        name="roasIapShare"
        control={control}
        render={({ field }) => (
          <FormControl isInvalid={!!errors.roasIapShare}>
            <FormLabel>
              ROAS IAP Share (%)
              <Tooltip label="How high is the share of IAP revenue in comparison to IAA revenue, e.g. if the partner tells you our game makes 90% revenue through IAP and 10% through IAA, you add '90' here">
                <InfoIcon ml={2} mb={1} color="blue.500" />
              </Tooltip>
            </FormLabel>
            <Input
              {...field}
              type="number"
              min={0}
              max={100}
              placeholder="0"
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (value < 0 || value > 100) {
                  field.onChange("");
                }
              }}
            />
            <FormErrorMessage>{errors.roasIapShare?.message}</FormErrorMessage>
          </FormControl>
        )}
      />

      <Controller
        name="revenueEventName"
        control={control}
        render={({ field }) => (
          <FormControl isInvalid={!!errors.revenueEventName}>
            <FormLabel>Revenue Event Name</FormLabel>
            <Input {...field} placeholder="event_name" />
            <FormErrorMessage>
              {errors.revenueEventName?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      />

      <Flex alignItems={"center"} gap={4} gridColumn="1 / -1">
        <Controller
          name="isRevenueValueShared"
          control={control}
          render={({ field: { onChange, value, ref } }) => (
            <Checkbox
              onChange={(e) => onChange(e.target.checked)}
              isChecked={value}
              ref={ref}
            >
              Is Revenue Value Shared
            </Checkbox>
          )}
        />
      </Flex>
      <Divider gridColumn="1 / -1" my={4} />
      <Controller
        name="repeatableSetup.repeatableEventName"
        control={control}
        rules={{ required: "Repeatable Event Name is required" }}
        render={({ field }) => (
          <FormControl
            isInvalid={!!errors.repeatableSetup?.repeatableEventName}
          >
            <FormLabel>Recurring Event Name</FormLabel>
            <Input
              {...field}
              placeholder="e.g. af_level_complete / <adjust_token>"
            />
            <FormErrorMessage>
              {errors.repeatableSetup?.repeatableEventName?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      />

      <Controller
        name="repeatableSetup.repeatableUnit"
        control={control}
        rules={{ required: "Repeatable Unit is required" }}
        render={({ field }) => (
          <FormControl isInvalid={!!errors.repeatableSetup?.repeatableUnit}>
            <FormLabel>Recurring Event Unit + Verb</FormLabel>
            <Input
              {...field}
              placeholder="e.g. Levels Finished or Areas Completed etc."
            />
            <FormErrorMessage>
              {errors.repeatableSetup?.repeatableUnit?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      />

      <Controller
        name="repeatableSetup.repeatableFinishedByAM"
        control={control}
        rules={{
          required: "Recurring Events Finished By AM is required",
        }}
        render={({ field }) => (
          <FormControl
            isInvalid={!!errors.repeatableSetup?.repeatableFinishedByAM}
          >
            <FormLabel>
              How often did you finish the recurring event within 15 minutes?
              <Tooltip label="Please download the game and play for 15 minutes and add here how often you finished the event within 15 minutes.">
                <InfoIcon ml={2} mb={1} color="blue.500" />
              </Tooltip>
            </FormLabel>
            <Input {...field} type="number" min={1} placeholder="1" />
            <FormErrorMessage>
              {errors.repeatableSetup?.repeatableFinishedByAM?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      />
      <Divider gridColumn="1 / -1" my={4} />
      <Flex gridColumn="1 / -1">
        <MultiInputEntries
          width={"95%"}
          sectionTitle="ROAS Goals"
          handleFieldChange={handleGoalsFieldChange}
          handleSave={() => {}}
          onRemove={handleGoalsRemove}
          addNewItem={addNewGoalItem}
          items={roasGoals}
          inputFields={[
            {
              title: "Day",
              leftAddon: "D",
              inputType: "numeric",
              min: 0,
              max: 365 * 2,
              keyName: "dx",
              placeHolder: "Day",
            },
            {
              title: "ROAS",
              leftAddon: "%",
              inputType: "numeric",
              min: 0,
              keyName: "roasGoal",
              placeHolder: "Goal",
            },
          ]}
          defaultEditableValue={false}
        />
      </Flex>
      <Flex
        mt={8}
        alignItems="center"
        justifyContent={"flex-end"}
        gridColumn="1 / -1"
      >
        <Button
          type="submit"
          colorScheme="brand"
          variant={"ghost"}
          onClick={handleSubmit(onSave)}
          px={8}
          isLoading={isSaving}
          isDisabled={
            isSaving ||
            (roasGoals?.length > 0 &&
              roasGoals?.some((g) => !g.dx || !g.roasGoal))
          }
        >
          Save
        </Button>
        <Button
          type="submit"
          colorScheme="brand"
          onClick={handleSubmit(onSubmit)}
          px={8}
          isLoading={isSubmitting}
          isDisabled={
            isSubmitting ||
            (roasGoals?.length > 0 &&
              roasGoals?.some((g) => !g.dx || !g.roasGoal))
          }
        >
          Submit
        </Button>
      </Flex>
      {ConfirmDialog}
    </SimpleGrid>
  );
};

export default RepeatableSetupForm;
